import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND, NAV } from "@/data";
import { EN, EN_NAV, FR_TO_EN, EN_TO_FR } from "@/en";

const testid = (path) => (path === "/" || path === "/en" ? "accueil" : path.replace(/\//g, ""));

function LangSwitch({ isEn, pathname, mobile }) {
  const norm = pathname !== "/" ? pathname.replace(/\/+$/, "") : pathname;
  const frTarget = isEn ? (EN_TO_FR[norm] || "/") : norm;
  const enTarget = isEn ? norm : (FR_TO_EN[norm] || "/en");
  const cls = (active) => `text-sm tracking-wide ${active ? "text-forest font-semibold" : "text-charcoal/60 hover:text-forest"}`;
  return (
    <div data-testid={mobile ? "lang-switch-mobile" : "lang-switch"} className="flex items-center gap-2">
      <Link to={frTarget} data-testid="lang-fr" className={cls(!isEn)}>Français</Link>
      <span className="text-charcoal/30">|</span>
      <Link to={enTarget} data-testid="lang-en" className={cls(isEn)}>English</Link>
    </div>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isEn = pathname === "/en" || pathname.startsWith("/en/");
  const items = isEn ? EN_NAV : NAV;
  const cta = isEn ? { to: "/en/contact", label: "Book" } : { to: "/contact#reserver", label: "Réserver" };
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 bg-cream/95 backdrop-blur-md border-b border-sand">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to={isEn ? "/en" : "/"} data-testid="brand-logo" className="leading-none">
          <span className="font-serif text-2xl lg:text-[26px] text-forest block">{BRAND.short}</span>
          <span className="text-[10px] uppercase tracking-[0.32em] text-brown/70">{isEn ? EN.brandPlace : BRAND.place}</span>
        </Link>

        <nav className="hidden xl:flex items-center gap-7">
          {items.map((n) => (
            <NavLink key={n.path} to={n.path} end={n.path === "/en"}
              data-testid={`nav-${testid(n.path)}`}
              className={({ isActive }) => `text-sm tracking-wide transition-colors duration-300 hover:text-forest ${isActive ? "text-forest font-medium" : "text-charcoal/70"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-5">
          <LangSwitch isEn={isEn} pathname={pathname} />
          <Link to={cta.to}>
            <Button data-testid="header-cta" className="rounded-full bg-forest hover:bg-forest-dark text-cream px-6">{cta.label}</Button>
          </Link>
        </div>

        <button data-testid="mobile-menu-toggle" className="xl:hidden text-forest" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden bg-cream border-t border-sand px-6 py-5 flex flex-col gap-4">
          {items.map((n) => (
            <NavLink key={n.path} to={n.path} end={n.path === "/en"} data-testid={`mobile-nav-${testid(n.path)}`} className="text-charcoal/80 py-1">
              {n.label}
            </NavLink>
          ))}
          <div className="pt-2 border-t border-sand"><LangSwitch isEn={isEn} pathname={pathname} mobile /></div>
          <Link to={cta.to}><Button className="rounded-full bg-forest text-cream w-full mt-1">{cta.label}</Button></Link>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const { pathname } = useLocation();
  const isEn = pathname === "/en" || pathname.startsWith("/en/");

  return (
    <footer data-testid="site-footer" className="bg-forest text-cream/80 mt-auto">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-3 gap-10">
        <div>
          <p className="font-serif text-2xl text-cream">{BRAND.name}</p>
          <p className="text-sm mt-2 text-cream/70">Chez Anne · Au 19</p>
          <p className="text-sm text-cream/70">{isEn ? EN.footer.place : BRAND.place}</p>
        </div>
        <div className="space-y-2 text-sm">
          <a data-testid="footer-phone" href={BRAND.phoneLink} className="flex items-center gap-2 hover:text-cream"><Phone className="w-4 h-4" strokeWidth={1.5} /> {BRAND.phone}</a>
          <a data-testid="footer-email" href={BRAND.emailLink} className="flex items-center gap-2 hover:text-cream"><Mail className="w-4 h-4" strokeWidth={1.5} /> {BRAND.email}</a>
        </div>
        <div className="flex flex-col gap-2 text-sm">
          {isEn ? (
            <>
              {EN.footer.links.map((l) => (
                <Link key={l.to} to={l.to} data-testid={`footer-${testid(l.to)}`} className="hover:text-cream">{l.label}</Link>
              ))}
              <Link to={EN.footer.legalTo} data-testid="footer-mentions" className="hover:text-cream">{EN.footer.legal}</Link>
              <Link to={EN.footer.privacyTo} data-testid="footer-confidentialite" className="hover:text-cream">{EN.footer.privacy}</Link>
            </>
          ) : (
            <>
              <Link to="/mentions-legales" data-testid="footer-mentions" className="hover:text-cream">Mentions légales</Link>
              <Link to="/politique-de-confidentialite" data-testid="footer-confidentialite" className="hover:text-cream">Politique de confidentialité</Link>
              <Link to="/contact" data-testid="footer-contact" className="hover:text-cream">Contact</Link>
            </>
          )}
        </div>
      </div>
      <div className="border-t border-cream/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 text-xs text-cream/40">
          © {new Date().getFullYear()} {BRAND.name} · {isEn ? EN.footer.rights : "Tanlay, Bourgogne"}
        </div>
      </div>
    </footer>
  );
}
